<template>
    <v-app>
        <Toolbar
            :drawer="drawer"
            :main-loader="mainLoader"
            @open-change-password-dialog="openChangePasswordDialog"
            @toggle-drawer="toggleDrawer"
        />

        <Content />

        <Footer />

        <Sidebar v-if="loggedIn" v-model:drawer="drawer" />

        <Snackbar />

        <ReleaseNotesDialog v-model="releaseNotesStore.showDialog" :latest-version="latestVersionData" />

        <AppLogoLoaderOverlay :model-value="circularLoader" persistent />

        <v-overlay :model-value="mainLoader" opacity="0" persistent :close-on-back="false" />

        <ChangePasswordDialog v-if="changePasswordDialog" :is-open="changePasswordDialog" @on-close="closeChangePasswordDialog" />
    </v-app>
</template>

<script setup lang="ts">
/*
    Organization / ordering applied:
    1. Imports
    2. Props / Emits
    3. Constants
    4. Stores & top-level services
    5. Reactive state (refs/reactive)
    6. Validation rules & setup
    7. Computed
    8. Handlers / methods
    9. Helpers / lifecycle
*/

// 1) Imports
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useDisplay } from 'vuetify'

import AppLogoLoaderOverlay from '@/components/common/AppLogoLoaderOverlay.vue'
import Content from '@/components/ContentComponent.vue'
import Footer from '@/components/FooterComponent.vue'
import ReleaseNotesDialog from '@/components/ReleaseNotesDialog.vue'
import Sidebar from '@/components/SidebarComponent.vue'
import Snackbar from '@/components/SnackbarComponent.vue'
import Toolbar from '@/components/ToolbarComponent.vue'
import { errorHandler } from '@/composables/errorHandler'
import { hasPermission } from '@/composables/usePermissionHandler'
import { useCommonStore, useNotesStore, useReleaseNotesStore, useUserStore } from '@/stores'
import type { UserData } from '@/stores/user/state'
import { useUsersService } from '@/views/Administration/Users/services/usersService'

const ChangePasswordDialog = defineAsyncComponent(() => import('@/views/User/ChangePasswordDialog.vue'))

// 2) Props / Emits
// (none in this component)

// 3) Constants
const route = useRoute()
const { mobile } = useDisplay()

// 4) Stores & top-level services
const userStore = useUserStore()
const commonStore = useCommonStore()
const notesStore = useNotesStore()
const releaseNotesStore = useReleaseNotesStore()
const usersService = useUsersService()

// 5) Reactive state (refs/reactive)
const drawer = ref(true)
const changePasswordDialog = ref(false)

// 6) Validation rules & setup
// (none in this component)

// 7) Computed
const mainLoader = computed(() => {
    return commonStore.mainLoader
})
const circularLoader = computed(() => {
    return commonStore.circularLoader
})
const loggedIn = computed(() => {
    return userStore.loggedIn
})
const user = computed<UserData | null>(() => {
    return userStore.user
})
const latestVersionData = computed(() => {
    return releaseNotesStore.latestVersionData
})
const isLastSystemVersionRead = computed(() => {
    if (!user.value) return true
    if (user.value.profile) return Boolean(user.value.profile.lastSystemVersionRead)
    return Boolean(user.value.lastSystemVersionRead)
})

// 8) Handlers / methods
function toggleDrawer(value: boolean) {
    drawer.value = value
}
function openChangePasswordDialog() {
    changePasswordDialog.value = true
}
function closeChangePasswordDialog() {
    changePasswordDialog.value = false
}

async function initializeApp() {
    try {
        // Load error codes when the app mounts
        await commonStore.getErrorCodes()

        if (userStore.loggedIn && userStore.userId && !userStore.user) {
            const response = await usersService.fetchUserById(userStore.userId)
            userStore.getUser(response.data)
        }
    } catch (error) {
        // Error codes loading failed, but the app should continue
        errorHandler(error)
    }
}

async function fetchCurrentUser(): Promise<void> {
    if (!userStore.userId) {
        return
    }

    const response = await usersService.fetchUserById(userStore.userId)
    userStore.getUser(response.data)
}

async function showReleaseNotesDialog(): Promise<void> {
    await releaseNotesStore.fetchLatestVersion()
    releaseNotesStore.setShowDialog(true)
}

// 9) Helpers / lifecycle
onMounted(() => {
    // Initialize app data
    initializeApp()
})

watch(
    () => route.fullPath,
    () => {
        if (mobile.value) drawer.value = false
    }
)

// Watch for login state changes to fetch unread notes count
watch(
    () => loggedIn.value,
    async (isLoggedIn: boolean) => {
        if (!isLoggedIn) {
            return
        }

        try {
            await fetchCurrentUser()

            if (userStore.userId && hasPermission(['production_sites_notes_admin_read', 'production_sites_note_leader_read'])) {
                await notesStore.fetchUnreadProductionSiteNotes(userStore.userId)
            }
        } catch (error) {
            // Silently fail - extra app data is not critical
            errorHandler(error)
        }
    },
    { immediate: true }
)

watch(
    () => user.value,
    async (newUser) => {
        if (newUser && !isLastSystemVersionRead.value) {
            try {
                await showReleaseNotesDialog()
            } catch (error) {
                errorHandler(error)
            }
        }
    }
)
</script>

<style lang="scss" scoped>
/* No component-specific styles */
</style>
